import React, { useEffect, useMemo, useState } from 'react';
import {
  operatorCompleteBooking,
  formatClockHMS,
  runningElapsedMs,
  msToHmsParts,
  hmsPartsToMs,
  hmsPartsToDecimalHours,
  formatSignedHmsDiff,
} from '../../services/bookingFlowApi';
import { useLanguage } from '../../context/LanguageContext';
import { formatTranslation } from '../../i18n/formatTranslation';
import TimeHmsScrollPicker from './TimeHmsScrollPicker';

export default function OperatorCompleteCard({ booking, onDone }) {
  const { t } = useLanguage();
  const ft = (key, vars) => formatTranslation(t(key), vars);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);

  const status = String(booking?.status || '');
  const isActive = booking && (status === 'Dispatched' || (status === 'Active' && booking.machine_started_at));

  const liveMs = useMemo(
    () => (isActive && booking?.machine_started_at ? runningElapsedMs(booking) : 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [booking, isActive, tick],
  );

  const [operatorHms, setOperatorHms] = useState(() => msToHmsParts(liveMs || 0));

  useEffect(() => {
    if (!isActive) return undefined;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;
    setOperatorHms(msToHmsParts(liveMs));
  // Only seed once when assignment first loads
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.id, isActive]);

  if (!isActive) return null;

  const operatorMs = hmsPartsToMs(operatorHms.h, operatorHms.m, operatorHms.s);
  const diffMs = operatorMs - liveMs;
  const decimalHours = hmsPartsToDecimalHours(operatorHms.h, operatorHms.m, operatorHms.s);
  const hasEntry = operatorMs > 0;

  const diffColor = diffMs === 0
    ? '#4CAF50'
    : Math.abs(diffMs) <= 15 * 60 * 1000
      ? '#c9a84c'
      : '#FF9800';

  const diffHint = diffMs === 0
    ? 'Matches machine live timer'
    : diffMs > 0
      ? 'You logged more than machine timer'
      : 'You logged less than machine timer';

  const handleSubmit = async () => {
    if (!hasEntry || decimalHours <= 0) {
      alert(t('bf2ActualWorkRequired'));
      return;
    }
    const ref = booking.booking_ref || booking.id;
    const timeLabel = formatClockHMS(operatorMs);
    if (!window.confirm(ft('bf2LogWorkConfirm', {
      time: timeLabel,
      hours: decimalHours,
      ref,
    }))) {
      return;
    }
    setBusy(true);
    try {
      await operatorCompleteBooking(booking.id, decimalHours, notes);
      onDone?.();
    } catch (err) {
      alert(err.message || t('bf2MarkCompleteFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: '12px', padding: '12px', borderRadius: '10px', border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(0,0,0,0.2)' }}>
      <p style={{ color: '#c9a84c', fontWeight: 700, fontSize: '12px', margin: '0 0 10px' }}>Mark Work Complete</p>

      <div style={{
        marginBottom: '12px',
        padding: '10px',
        borderRadius: '8px',
        background: 'rgba(0,0,0,0.28)',
        border: '1px solid rgba(201,168,76,0.15)',
      }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
          <div>
            <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 2px', textTransform: 'uppercase' }}>Machine live</p>
            <p style={{ color: '#4CAF50', fontSize: '16px', fontWeight: 800, margin: 0, fontVariantNumeric: 'tabular-nums' }}>
              {formatClockHMS(liveMs)}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 2px', textTransform: 'uppercase' }}>Your entry</p>
            <p style={{ color: '#E8D5A3', fontSize: '16px', fontWeight: 800, margin: 0, fontVariantNumeric: 'tabular-nums' }}>
              {formatClockHMS(operatorMs)}
            </p>
          </div>
        </div>
        <div style={{
          padding: '8px',
          borderRadius: '6px',
          background: 'rgba(201,168,76,0.08)',
          border: `1px solid ${diffColor}55`,
        }}
        >
          <p style={{ color: '#8896a8', fontSize: '10px', margin: '0 0 2px' }}>Difference (your entry − machine live)</p>
          <p style={{ color: diffColor, fontSize: '14px', fontWeight: 700, margin: 0, fontVariantNumeric: 'tabular-nums' }}>
            {formatSignedHmsDiff(diffMs)}
          </p>
          <p style={{ color: '#8896a8', fontSize: '10px', margin: '4px 0 0' }}>{diffHint}</p>
        </div>
      </div>

      <p style={{ color: '#8896a8', fontSize: '11px', margin: '0 0 6px' }}>
        Actual hours worked * <span style={{ color: '#c9a84c' }}>(scroll to adjust)</span>
      </p>
      <TimeHmsScrollPicker
        hours={operatorHms.h}
        minutes={operatorHms.m}
        seconds={operatorHms.s}
        maxHours={99}
        onChange={({ hours, minutes, seconds }) => setOperatorHms({ h: hours, m: minutes, s: seconds })}
      />
      <p style={{ color: '#8896a8', fontSize: '10px', margin: '6px 0 0', textAlign: 'center' }}>
        Billing: {decimalHours} hrs
      </p>

      <label style={{ color: '#8896a8', fontSize: '11px', marginTop: '10px', display: 'block' }}>
        Notes (optional)
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px', background: '#0a1628', border: '1px solid rgba(201,168,76,0.2)', color: '#e8e0d0', borderRadius: '6px' }}
        />
      </label>
      <button
        type="button"
        disabled={busy || !hasEntry}
        onClick={handleSubmit}
        style={{
          marginTop: '10px',
          width: '100%',
          padding: '10px',
          background: hasEntry ? 'rgba(76,175,80,0.2)' : 'rgba(100,100,100,0.2)',
          border: `1px solid ${hasEntry ? '#4CAF50' : '#666'}`,
          color: hasEntry ? '#4CAF50' : '#888',
          borderRadius: '8px',
          cursor: busy ? 'wait' : 'pointer',
          fontWeight: 700,
        }}
      >
        Mark Work Complete
      </button>
    </div>
  );
}
